import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Typography } from '@mui/material';

import { getMousePos } from '../utils/canvasFunctions';

/**
 * Shows the map pixel under the cursor. The canvas has no CSS scaling, so 1 CSS px = 1 map px.
 * @param {React.RefObject} canvasRef - Ref to the map canvas.
 */
export default function CursorCoordinates({ canvasRef }) {
  const [position, setPosition] = useState(null);
  const lastCursorRef = useRef(null);
  const { t } = useTranslation();

  useEffect(() => {
    const canvas = canvasRef.current;
    const updatePosition = () => {
      const { x, y } = getMousePos(canvas, lastCursorRef.current);
      setPosition({ x: Math.floor(x), y: Math.floor(y) });
    };
    const onMouseMove = (event) => {
      lastCursorRef.current = { clientX: event.clientX, clientY: event.clientY };
      updatePosition();
    };
    const onMouseLeave = () => {
      lastCursorRef.current = null;
      setPosition(null);
    };
    // Scrolling (map panning or the page) moves the map under a still cursor; scroll does not
    // bubble, so it is captured at document level to cover both.
    const onScroll = () => {
      if (lastCursorRef.current) updatePosition();
    };
    canvas.addEventListener('mousemove', onMouseMove);
    canvas.addEventListener('mouseleave', onMouseLeave);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      canvas.removeEventListener('mousemove', onMouseMove);
      canvas.removeEventListener('mouseleave', onMouseLeave);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [canvasRef]);

  if (!position) return null;
  return (
    <Typography
      variant="caption"
      sx={{
        position: 'absolute',
        bottom: '10px',
        right: '10px',
        zIndex: 1000,
        px: 1,
        borderRadius: 1,
        boxShadow: 1,
        bgcolor: 'background.paper',
        pointerEvents: 'none',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {t('overseer_app.layouts.cursor_coordinates', 'x: {{x}}, y: {{y}} px', position)}
    </Typography>
  );
}
